import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Shield, Clock, TrendingUp } from 'lucide-react';
import { mockStats, mockCompanyInfo } from '../mock';

const AboutUs = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero with image */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eXNjYXBlJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc0NTI0OTY0fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 to-slate-900/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 py-28">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-teal-300 mb-4 block">About Us</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Built for<br />Business Owners
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-xl">
              We're dedicated to Cashflow Engineering, providing business owners and referral partners with financing solutions that fuel growth, expansion, and long term success.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="bg-white border border-slate-200 rounded-2xl grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200">
            {[
              { label: 'Capital Deployed', value: mockStats.capitalDeployed, icon: <TrendingUp className="h-5 w-5" /> },
              { label: 'Businesses Funded', value: mockStats.businessesFunded, icon: <Users className="h-5 w-5" /> },
              { label: 'Avg. Approval', value: mockStats.averageApproval, icon: <Clock className="h-5 w-5" /> },
              { label: 'Satisfaction', value: mockStats.clientSatisfaction, icon: <Shield className="h-5 w-5" /> },
            ].map((s) => (
              <div key={s.label} className="py-8 px-6 text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission with image */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Our Mission</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Cashflow Engineering: Our Core Philosophy
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                At Quidity Direct, we believe in Cashflow Engineering. We understand the unique challenges entrepreneurs face. Our financing solutions are designed specifically for business owners who need capital to grow, expand, and seize opportunities.
              </p>
              <p className="text-slate-500 leading-relaxed mb-8">
                With a team of experienced financial professionals and a streamlined process, we've helped over 1,000 businesses access the capital they need, quickly and transparently.
              </p>
              <Link to="/apply" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-800 transition-all">
                Get Started <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {/* Image instead of plain gradient */}
            <div className="relative rounded-2xl overflow-hidden h-[400px]">
              <img src="https://images.pexels.com/photos/5414060/pexels-photo-5414060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Business owner" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-3 block">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Why Businesses Choose Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Clock className="h-7 w-7 text-[#115E59]" />, title: 'Fast Process', desc: 'Streamlined applications with decisions in as little as 24 hours.', img: 'https://images.unsplash.com/photo-1772112334844-2eed0111e690?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwY29uZmVyZW5jZXxlbnwwfHx8fDE3NzQ1MjQ5NjV8MA&ixlib=rb-4.1.0&q=85' },
              { icon: <Shield className="h-7 w-7 text-[#115E59]" />, title: 'Transparent Terms', desc: 'No hidden fees or surprise charges. Clear terms from day one.', img: 'https://images.unsplash.com/photo-1764181725776-c40871005b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eXNjYXBlJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc0NTI0OTY0fDA&ixlib=rb-4.1.0&q=85' },
              { icon: <Users className="h-7 w-7 text-[#115E59]" />, title: 'Personal Service', desc: 'Dedicated team members work with you through every step.', img: 'https://images.pexels.com/photos/7289728/pexels-photo-7289728.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
            ].map((v) => (
              <div key={v.title} className="bento-card bg-white overflow-hidden group">
                <div className="relative h-40 overflow-hidden">
                  <img src={v.img} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-7">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4">{v.icon}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Offices */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Global Presence</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Offices Worldwide</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-2">Miami HQ</h3>
              <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-line">{mockCompanyInfo.address.street}{'\n'}{mockCompanyInfo.address.suite}{'\n'}{mockCompanyInfo.address.city}, {mockCompanyInfo.address.state} {mockCompanyInfo.address.zip}</p>
            </div>
            {mockCompanyInfo.additionalLocations.map((loc) => (
              <div key={loc.name} className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-900 mb-2">{loc.name}</h3>
                <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-line">{loc.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with image */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/4175028/pexels-photo-4175028.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ready to Work Together?</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-10">Whether you're a borrower or a referral partner, we'd love to help you grow.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="bg-teal-600 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-teal-500 transition-colors">Apply for Financing</Link>
            <Link to="/partner" className="border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Become a Partner</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
